/**
 * Development-build canary.
 *
 * Serves two purposes, both of which depend on this module being reachable only
 * from behind `__IMPERSONATION__`:
 *
 * 1. It tells anyone with a console open which flavour they are looking at,
 *    which matters once preprod and production run the same UI but not the same
 *    features.
 * 2. `scripts/check-build-flavour.ts` looks for {@link DEV_BUILD_MARKER} in
 *    production artifacts. Because this module is stripped from a production
 *    build, finding the marker there means a bundler lost its `define` and the
 *    development-only code it guards shipped. Without a canary that check would
 *    pass whether or not the flag worked.
 *
 * @module dev/build-canary
 */

/** Sentinel string the production build check searches for. */
export const DEV_BUILD_MARKER = "SOCRATIQ_DEV_FLAVOUR_BUILD";

/**
 * Sentinel string the production build check searches for, marking the
 * SQL-backed seed/reset capability (`src/db/seed`).
 *
 * Two capabilities sit behind `__SEED_DATA__` and are checked here: wiping
 * and reseeding a real, SQL-backed database, and opening the in-process SQLite
 * one. Neither may reach a production artifact, the first because it can
 * `DELETE` live tenant rows in Azure SQL, the second because a bundle that
 * can open a second database is one more thing a deployment can be pointed
 * at by mistake. The scan reads the bundle; `@mikro-orm/libsql` is a runtime
 * dependency and is installed into the image either way.
 */
export const SEED_DATA_MARKER = "SOCRATIQ_SEED_DATA_ENABLED";

/**
 * Announce that this bundle was built with the SQL seed/reset capability
 * compiled in. See {@link announceDevBuild}; same purpose, same mechanism.
 *
 * @param log - Logging function. Injected so tests do not write to the console.
 */
export function announceSeedDataBuild(log: (message: string) => void = console.info): void {
  log(`[${SEED_DATA_MARKER}] the SQL seed/reset capability is enabled in this build.`);
}

/**
 * Announce that this bundle was built with development-only features compiled
 * in.
 *
 * @param log - Logging function. Injected so tests do not write to the console.
 */
export function announceDevBuild(log: (message: string) => void = console.info): void {
  log(`[${DEV_BUILD_MARKER}] development features are enabled in this build.`);
}
